import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdOutlineNotificationsActive } from 'react-icons/md';
import BottomNavigation from '../../components/feature/BottomNavigation';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { useTutorial } from '../../tutorial/TutorialProvider';

export default function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { supported, permission, token, loading, error, requestAndSaveToken, removeToken } = usePushNotifications();
  const { resume } = useTutorial();

  const focus = useMemo(() => new URLSearchParams(location.search).get('focus'), [location.search]);
  const showPushAttentionIcon = !token;
  const pushSectionRef = useRef<HTMLDivElement | null>(null);
  const [pushFocusHighlight, setPushFocusHighlight] = useState(false);

  useEffect(() => {
    if (focus !== 'push') return;
    const el = pushSectionRef.current;
    try { el?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* ignore */ }
    setPushFocusHighlight(true);
    const t = window.setTimeout(() => setPushFocusHighlight(false), 3000);
    return () => window.clearTimeout(t);
  }, [focus]);

  const settingsOptions = [
    {
      id: 'profile',
      title: 'Perfil',
      description: 'Gerencie suas informações pessoais e da família',
      icon: 'ri-user-line',
      color: 'text-blue-500',
      bgColor: 'bg-blue-100',
      route: '/profile'
    },
    {
      id: 'privacy',
      title: 'Política de Privacidade',
      description: 'Leia nossa política de privacidade',
      icon: 'ri-shield-line',
      color: 'text-green-500',
      bgColor: 'bg-green-100',
      route: '/terms-and-privacy'
    },
    {
      id: 'terms',
      title: 'Termos de Uso',
      description: 'Consulte os termos de uso do aplicativo',
      icon: 'ri-file-text-line',
      color: 'text-purple-500',
      bgColor: 'bg-purple-100',
      route: '/terms-and-privacy'
    },
    {
      id: 'feedback',
      title: 'Feedback',
      description: 'Envie suas sugestões e comentários',
      icon: 'ri-message-3-line',
      color: 'text-orange-500',
      bgColor: 'bg-orange-100',
      route: '/feedback'
    },
    {
      id: 'rate',
      title: 'Avalie-nos',
      description: 'Avalie nossa aplicação na loja',
      icon: 'ri-star-line',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-100',
      route: '/rate-us'
    },
    {
      id: 'help',
      title: 'Centro de Ajuda',
      description: 'Encontre respostas para suas dúvidas',
      icon: 'ri-question-line',
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-100',
      route: '/help-center'
    }
  ];

  const handleOptionClick = (route: string) => {
    navigate(route);
  };

  // Estado derivado: ativo quando há token e permissão concedida
  const isActive = !!token && permission === 'granted';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="flex items-center justify-between p-6">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center"
          >
            <i className="ri-arrow-left-line text-gray-600 text-lg"></i>
          </button>
          <h1 className="text-xl font-semibold text-gray-800">Configurações</h1>
          <div className="w-10 h-10"></div> {/* Spacer */}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {/* Guided Tutorial */}
        <div className="bg-white rounded-3xl p-6 shadow-sm mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Tutorial Guiado</h2>
          <p className="text-sm text-gray-500 mb-4">Reabra o tour guiado para revisitar os passos principais do app.</p>
          <button
            onClick={() => resume('first_run')}
            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Reabrir Guided Tutorial
          </button>
        </div>
        {/* Notifications */}
        <div
          ref={pushSectionRef}
          className={`bg-white rounded-3xl p-6 shadow-sm mb-6 ${pushFocusHighlight ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-800">Notificações</h2>
            {showPushAttentionIcon && (
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-[0_6px_16px_rgba(239,68,68,0.35)] border border-red-100">
                <MdOutlineNotificationsActive className="h-4 w-4 text-red-500 animate-wiggle" />
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Ative para receber lembretes e avisos importantes dos tratamentos. {supported ? '' : 'Seu navegador pode não suportar push.'}
          </p>
          <div className="flex items-center gap-3">
            <button
              disabled={loading || permission === 'denied' || isActive}
              onClick={requestAndSaveToken}
              className={`px-4 py-2 rounded-xl ${
                isActive
                  ? 'bg-emerald-100 text-emerald-700 cursor-default'
                  : loading || permission === 'denied'
                  ? 'bg-gray-300 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              } disabled:opacity-60`}
              data-tutorial-anchor="btn-enable-push"
            >
              {loading
                ? 'Ativando...'
                : permission === 'denied'
                ? 'Bloqueado'
                : isActive
                ? 'Ativado'
                : 'Ativar'}
            </button>
            <button
              disabled={loading || !token}
              onClick={removeToken}
              className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white disabled:opacity-60"
            >
              Desativar
            </button>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            <div>Status: {isActive ? 'ligado' : 'desligado'}</div>
            {error && <div className="text-red-600">{error}</div>}
          </div>
        </div>
        <div className="space-y-4">
          {settingsOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleOptionClick(option.route)}
              className="w-full bg-white rounded-3xl p-6 shadow-sm hover:shadow-md transition-all duration-200 active:scale-95"
            >
              <div className="flex items-center">
                <div className={`w-12 h-12 ${option.bgColor} rounded-2xl flex items-center justify-center mr-4`}>
                  <i className={`${option.icon} ${option.color} text-xl`}></i>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{option.title}</h3>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
                <div className="ml-4">
                  <i className="ri-arrow-right-s-line text-gray-400 text-xl"></i>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* App Info */}
        <div className="mt-8 bg-white rounded-3xl p-6 shadow-sm mb-24">
          <div className="text-center">
            <div className="w-16 h-16 rounded-3xl flex items-center justify-center mb-4 mx-auto overflow-hidden">
              <img 
                src="/Imagens/logo 2.0.png" 
                alt="Logo Magistral 2.0" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallbackDiv = document.createElement('div');
                  fallbackDiv.className = 'w-16 h-16 bg-emerald-100 rounded-3xl flex items-center justify-center';
                  const icon = document.createElement('i');
                  icon.className = 'ri-heart-pulse-line text-emerald-500 text-2xl';
                  fallbackDiv.appendChild(icon);
                  target.parentNode?.appendChild(fallbackDiv);
                }}
              />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Magistral</h3>
            <p className="text-sm text-gray-500 mb-1">Versão 1.0.0</p>
            <p className="text-xs text-gray-400">Desenvolvido com ❤️ para sua saúde</p>
          </div>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
}
